import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Smartphone, Store, Timer, MapPin, QrCode, BadgeCheck, Download } from "lucide-react";
import { PhoneMockup } from "./PhoneMockup";
import { fadeUp } from "./GlassCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stats = [
  { icon: Timer, value: "90 Days", label: "Free Trial" },
  { icon: MapPin, value: "3-5 KM", label: "Hyper-local Reach" },
  { icon: QrCode, value: "2 Min", label: "Zero-code Setup" },
  { icon: BadgeCheck, value: "100%", label: "Secure UPI" },
];

export const Hero = () => {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    axios.get(`${API}/app-meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const android = meta?.android;
  const statLine = android
    ? `Latest v${android.version} | ${android.size_mb} MB${android.filename ? ` | ${android.filename}` : ""}${android.downloads > 0 ? ` | ${android.downloads.toLocaleString("en-IN")} downloads` : ""}`
    : "Latest v1.3 | 99 MB";

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
        <div>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/[0.08] backdrop-blur-xl px-4 py-1.5 text-xs font-medium tracking-wide text-amber-300"
            >
              <Store size={13} /> India's B2B + B2C commerce OS
            </span>
          </motion.div>

          <motion.h1
            data-testid="hero-headline"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.04]"
          >
            From Local to National. <span className="gold-text">Every Market in India, Now on Your Phone.</span>
          </motion.h1>

          <motion.p
            data-testid="hero-subheadline"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="mt-6 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed"
          >
            Whether it's your trusted neighborhood store or a famous shop across the country—order directly.
            Get the best prices with zero middlemen and experience a whole new way to shop across India.
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="/api/download/apk-android"
              data-testid="hero-get-app-button"
              className="group relative inline-flex items-center gap-3 rounded-full bg-amber-400 px-10 py-5 text-base font-bold text-[#0b0f19] shadow-[0_0_40px_rgba(251,191,36,0.45)] hover:bg-amber-300 hover:shadow-[0_0_70px_rgba(251,191,36,0.65)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
            >
              <span className="absolute inset-0 rounded-full bg-amber-300/40 blur-xl -z-10 animate-glow" />
              <Download size={20} className="group-hover:translate-y-0.5 transition-transform duration-300" /> Get the App
            </a>
            <div data-testid="hero-app-stats" className="text-sm text-slate-400">
              <p className="font-semibold text-amber-300">{statLine}</p>
              <p className="text-xs mt-0.5">Direct download · Android APK &amp; iOS</p>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-8">
            <a
              href="#partner"
              data-testid="hero-list-store-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-xl px-8 py-3.5 text-sm font-semibold text-white hover:border-amber-400/40 hover:bg-white/[0.08] hover:-translate-y-0.5 transition-[border-color,background-color,transform] duration-300"
            >
              <Store size={16} className="text-amber-400" /> List Your Store Free
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={5}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl"
            data-testid="hero-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="group">
                <s.icon size={18} className="text-amber-400 mb-2 group-hover:scale-110 transition-transform duration-300" />
                <p className="font-display text-xl font-semibold">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 48, rotateY: -8 }}
          animate={{ opacity: 1, y: 0, rotateY: 0 }}
          transition={{ delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center lg:justify-end [perspective:1000px]"
        >
          <PhoneMockup />
        </motion.div>
      </div>
    </section>
  );
};
